import { Alert, Button, Card, CircularProgress, IconButton, Stack, Typography } from "@mui/material"
import { FormProvider, useForm } from "react-hook-form"
import { zodResolver } from '@hookform/resolvers/zod'
import { draftProjectValidationSchema } from "../ZodValidationSchema"
import { FormBlock } from "../../../../common/FormGenerator"
import { getFormConfig } from "./settingsConfig"
import { CardStyle, TypStyle } from "./style"
import { useCreateProjectMutation, useGetSocialOrderQuery } from "../../../../api"
import { useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"
import { useNavigate, useSearchParams } from "react-router-dom"
import { routesProfile } from "../../../../consts"
import { DescriptionSchemaType } from "../ZodValidationSchema/DescriptionSchema"
import { ProjectStageEnum, ProjectTypeEnum } from "../../../../consts/projectTypeOptions"
import { requiredTitle } from "../../../../utils/common"
import { IconClose } from "../../../../icons/IconClose"

function parseSocialOrderIdParam(raw: string | null): number | null {
    if (raw == null || raw.trim() === '') return null
    if (!/^\d+$/.test(raw.trim())) return null
    const id = Number(raw.trim())
    return Number.isFinite(id) && id > 0 ? id : null
}

export const DraftProjectForm = () => {
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const socialOrderIdFromUrl = parseSocialOrderIdParam(searchParams.get('socialOrderId'))
    const [linkedSocialOrderId, setLinkedSocialOrderId] = useState<number | null>(socialOrderIdFromUrl)
    const didPrefillRef = useRef(false)

    useEffect(() => {
        setLinkedSocialOrderId(socialOrderIdFromUrl)
        if (socialOrderIdFromUrl == null) {
            didPrefillRef.current = false
        }
    }, [socialOrderIdFromUrl])

    const { data: sourceSocialOrder } = useGetSocialOrderQuery(
        { id: linkedSocialOrderId as number },
        { skip: linkedSocialOrderId == null },
    )

    const methods = useForm<DescriptionSchemaType>({
        mode: 'onBlur',
        resolver: zodResolver(draftProjectValidationSchema),
        defaultValues: {
            name: "",
            type: ProjectTypeEnum.ONE_TIME,
            stage: ProjectStageEnum.IDEA,
            region: "",
            description: "",
            full_description: "",
            env_factor: "",
            expected_result: "",
            initial_state: "",
            necessary_actions: "",
            implementation: "",
        },
    })
    const { handleSubmit, setValue, getValues } = methods

    useEffect(() => {
        if (!sourceSocialOrder || didPrefillRef.current) return
        didPrefillRef.current = true

        const currentFullDescription = (getValues('full_description') ?? '').trim()
        const currentEnvFactor = (getValues('env_factor') ?? '').trim()

        // Не перезаписывать поля, которые пользователь уже успел заполнить до ответа SO.
        if (!currentFullDescription) {
            setValue('full_description', sourceSocialOrder.description ?? '', { shouldDirty: false })
        }
        if (!currentEnvFactor) {
            setValue('env_factor', sourceSocialOrder.problem ?? '', { shouldDirty: false })
        }
    }, [sourceSocialOrder, setValue, getValues])

    const handleUnlinkSocialOrder = () => {
        setLinkedSocialOrderId(null)
        const next = new URLSearchParams(searchParams)
        next.delete('socialOrderId')
        setSearchParams(next, { replace: true })
    }

    const [
        createProject,
        { 
            data: createProjectData, 
            error, 
            isError: isErrorCreateProject, 
            isSuccess: isSuccessCreateProject,
            isLoading: isLoadingCreateProject,
            reset, 
        },
    ] = useCreateProjectMutation()

    const onSubmitDraft = (data: DescriptionSchemaType) => {
        const fullDescriptionTrimmed = (data.full_description ?? "").trim()
        createProject({
            ...data,
            ...(fullDescriptionTrimmed ? { full_description: fullDescriptionTrimmed } : {}),
            ...(linkedSocialOrderId != null ? { social_order_id: linkedSocialOrderId } : {}),
        })
    }

    useEffect(() => {
        if (isErrorCreateProject) {
            toast.error('Не удалось создать проект')
            reset()
        }
    }, [error, isErrorCreateProject, reset])

    useEffect(() => {
        if (isSuccessCreateProject && createProjectData) {
            toast.success('Проект создан')
            navigate(`/${routesProfile.PROJECTS.path}/${createProjectData.id}/edit`)
        }
    }, [createProjectData, isSuccessCreateProject, navigate])

    return (
        <Stack alignItems="center" pt={{ xs: "15px", md: "50px" }} px={1.5}>
            <FormProvider {...methods}>
                <Stack gap={2} maxWidth={{ xs: "736px", md: "840px" }} width="100%">
                    <Typography
                        variant="h1"
                        px={{ xs: 0, md: 2.5 }}
                    >
                        Создание проекта
                    </Typography>
                    {linkedSocialOrderId != null ? (
                        <Alert
                            severity="info"
                            action={
                                <IconButton
                                    aria-label="Отвязать социальный заказ"
                                    color="inherit"
                                    size="small"
                                    onClick={handleUnlinkSocialOrder}
                                >
                                    <IconClose fontSize="small" />
                                </IconButton>
                            }
                        >
                            {sourceSocialOrder?.title
                                ? `Проект создаётся из соцзаказа «${sourceSocialOrder.title}»`
                                : 'Проект создаётся из соцзаказа'}
                        </Alert>
                    ) : null}
                    <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                        <Typography
                            variant="black-16-semiBold"
                            component="div"
                            sx={TypStyle}
                        >
                            {requiredTitle("Название проекта")}
                        </Typography>
                        <FormBlock formFieldConfig={getFormConfig('name')} />
                    </Card>
                    <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                        <Typography
                            variant="black-16-semiBold"
                            component="div"
                            sx={TypStyle}
                        >
                            Тип проекта
                        </Typography>
                        <FormBlock formFieldConfig={getFormConfig('type')} />
                    </Card>
                    <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                        <Typography
                            variant="black-16-semiBold"
                            component="div"
                            sx={TypStyle}
                        >
                            Этап проекта
                        </Typography>
                        <FormBlock formFieldConfig={getFormConfig("stage")} />
                    </Card>
                    <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                        <Typography
                            variant="black-16-semiBold"
                            component="div"
                            sx={TypStyle}
                        >
                            {requiredTitle("Местоположение проекта")}
                        </Typography>
                        <FormBlock formFieldConfig={getFormConfig("region")} />
                    </Card>
                    <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                        <Typography
                            variant="black-16-semiBold"
                            component="div"
                            sx={TypStyle}
                        >
                            {requiredTitle("Краткое описание")}
                        </Typography>
                        <FormBlock formFieldConfig={getFormConfig('description')} />
                    </Card>
                    <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                        <Typography
                            variant="black-16-semiBold"
                            component="div"
                            sx={TypStyle}
                        >
                            Полное описание
                        </Typography>
                        <FormBlock formFieldConfig={getFormConfig('full_description')} />
                    </Card>
                    <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                        <Typography
                            variant="black-16-semiBold"
                            component="div"
                            sx={TypStyle}
                        >
                            {requiredTitle("Проблема/возможность (фактор среды)")}
                        </Typography>
                        <FormBlock formFieldConfig={getFormConfig('env_factor')} />
                    </Card>
                    <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                        <Typography
                            variant="black-16-semiBold"
                            component="div"
                            sx={TypStyle}
                        >
                            {requiredTitle("Что должно получиться?")}
                        </Typography>
                        <FormBlock formFieldConfig={getFormConfig('expected_result')} />
                    </Card>
                    <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                        <Typography
                            variant="black-16-semiBold"
                            component="div"
                            sx={TypStyle}
                        >
                            {requiredTitle("Исходное положение дел")}
                        </Typography>
                        <FormBlock formFieldConfig={getFormConfig('initial_state')} />
                    </Card>
                    <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                        <Typography
                            variant="black-16-semiBold"
                            component="div"
                            sx={TypStyle}
                        >
                            {requiredTitle("Что необходимо сделать по проекту?")}
                        </Typography>
                        <FormBlock formFieldConfig={getFormConfig('necessary_actions')} />
                    </Card>
                    <Card variant="primaryAdaptiveCard" sx={CardStyle}>
                        <Typography
                            variant="black-16-semiBold"
                            component="div"
                            sx={TypStyle}
                        >
                            {requiredTitle("Как будем реализовывать?")}
                        </Typography>
                        <FormBlock formFieldConfig={getFormConfig('implementation')} />
                    </Card>

                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="action"
                            onClick={handleSubmit(onSubmitDraft)}
                            disabled={isLoadingCreateProject}
                        >
                            {isLoadingCreateProject ? (
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <CircularProgress size={16} color="inherit" />
                                    <span>Сохранение...</span>
                                </Stack>
                            ) : (
                                "Далее"
                            )}
                        </Button>
                    </Stack>
                </Stack>
            </FormProvider>
        </Stack>
    )
}
